import React, { useState, useRef } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography, Alert } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface QRCodeDisplayProps {
  qrUrl: string;
  redirectUrl: string;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({ qrUrl, redirectUrl }) => {
  const [enlargeOpen, setEnlargeOpen] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  const handleDownload = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = 512;
    canvas.height = 512;

    img.onload = () => {
      ctx?.drawImage(img, 0, 0, 512, 512);
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'qr-code.png';
          link.click();
          URL.revokeObjectURL(url);
        }
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <Box>
      <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
        QR code generated successfully. Scan to verify the redirect.
      </Alert>

      <Box
        ref={qrRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 2,
          p: 3,
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          cursor: 'pointer',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'scale(1.02)', boxShadow: 2 },
        }}
        onClick={() => setEnlargeOpen(true)}
      >
        <QRCodeSVG value={qrUrl} size={256} level="H" includeMargin />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} fullWidth sx={{ borderRadius: '8px', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", textTransform: 'none' }}>
          Download QR Code
        </Button>
        <Button variant="outlined" startIcon={<ZoomInIcon />} onClick={() => setEnlargeOpen(true)} fullWidth sx={{ borderRadius: '8px', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", textTransform: 'none' }}>
          View Larger
        </Button>
      </Box>

      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>QR Code URL:</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
          {qrUrl}
        </Typography>
      </Box>

      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Expected Redirect URL:</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
          {redirectUrl}
        </Typography>
      </Box>

      <Dialog open={enlargeOpen} onClose={() => setEnlargeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          QR Code
          <IconButton onClick={() => setEnlargeOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, backgroundColor: '#fff' }}>
            <QRCodeSVG value={qrUrl} size={400} level="H" includeMargin />
          </Box>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ borderRadius: '8px', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", textTransform: 'none' }}>
              Download
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default QRCodeDisplay;
